"use client";

import Link from "next/link";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ProductCard } from "@/components/ProductCard";
import { CartSummary } from "@/components/order/CartSummary";
import { cartItemCount, cartLines, cartTotal, useCartStore } from "@/lib/store/cart";
import type { Product } from "@/lib/types";

export function OrderBuilder({ products }: { products: Product[] }) {
  const items = useCartStore((state) => state.items);
  const setQuantity = useCartStore((state) => state.setQuantity);

  const lines = cartLines(items);
  const total = cartTotal(items);
  const count = cartItemCount(items);

  if (products.length === 0) {
    return (
      <p className="text-center text-muted-foreground">
        Nothing on the menu this week — check back soon!
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            mode="order"
            product={product}
            quantity={items[product.id]?.quantity ?? 0}
            onQuantityChange={(quantity) => setQuantity(product, quantity)}
          />
        ))}
      </div>

      <aside className="h-fit lg:sticky lg:top-6">
        <Card>
          <CardHeader>
            <h2 className="font-heading text-xl font-semibold">Your Order</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <CartSummary lines={lines} total={total} />
            {count > 0 ? (
              <Link href="/checkout" className={buttonVariants({ size: "lg", className: "w-full" })}>
                Continue
              </Link>
            ) : (
              <Button className="w-full" size="lg" disabled>
                Continue
              </Button>
            )}
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
